import React, {useCallback, useEffect, useMemo, useState} from "react";
import {FormModal} from "../../../../components/index.jsx";
import {NOOP} from "../../../../utils/NOOP.jsx";
import {useDebounceState, useRequest} from "../../../../utils/hooks.jsx";
import {isEmpty, set, unset} from "lodash";
import {createProductLink, selectServiceIdFromProduct} from "../../../MarketplaceHelper.jsx";
import {getProviderProduct} from "../../../MarketplaceProviderPage/utils.js";
import {ConfigProvider, message} from "antd";
import {StringBuilder} from "../../../../AppUtils.js";
import {openWindow} from "../../../../http/WindowOpener.jsx";
import "../../marketplace-booking.css";

export const ProductBookingAsClientModal = ({visible, onClose = NOOP}) => {
    const [formValues, setFormValues] = useState([]);

    const onInnerClose = useCallback(() => {
        setFormValues(prev => ({}));
        setUnDebouncedUserEmail("");
        onClose();
    }, [onClose]);

    const [debouncedUserEmail, setUnDebouncedUserEmail] = useDebounceState("", 200);

    const [providers = []] = useRequest("/api/providers/info", "POST", {
        projection: {
            provider: ["providerId", "businessName"],
            product: ["productId", "productName"]
        }
    });

    const [workspaces = [], isLoadingWorkspaces] = useRequest(
        `/api/users/workspaces`,
        "POST",
        {
            email: debouncedUserEmail
        },
        [debouncedUserEmail],
        !!debouncedUserEmail,
        null,
        []
    );

    const [userMe = {}] = useRequest(
        `/api/users/me/${formValues?.userId}`,
        "GET",
        null,
        [formValues?.userId],
        !!formValues?.userId,
        null,
        {}
    );

    const [services = []] = useRequest(
        `/api/categories/users/${formValues?.userId}`,
        "GET",
        null,
        [formValues?.userId],
        !!formValues?.userId,
        null,
        []
    );

    const [providerInfo = {}, isLoadingProvider] = useRequest(
        `/api/providers/${formValues?.providerId}`,
        "get",
        null,
        [formValues?.providerId],
        !!formValues?.providerId,
        null,
        {}
    );

    const providerProducts = useMemo(
        () => getProviderProduct(userMe, providerInfo, services, false),
        [providerInfo, services, userMe]
    );

    useEffect(() => {
        if (formValues?.email !== debouncedUserEmail) {
            setFormValues({email: formValues?.email});
        }
        if (formValues?.email) {
            setUnDebouncedUserEmail(formValues?.email);
        }
    }, [formValues?.email]);

    const onBook = useCallback(() => {
        const product = providerProducts?.find(({service}) => service.productId === formValues?.productId);

        if (!product) {
            message.error("Product not found!");
            return;
        }

        if (product.isProductBundle) {
            return;
        }

        const serviceId = selectServiceIdFromProduct(userMe, product, services);
        const url = createProductLink(product, {me: userMe, services, serviceId});
        openWindow(`${url}/booking/user/${userMe?.userId}`);
        onInnerClose();
    }, [providerProducts, userMe, services, onInnerClose, formValues?.productId]);

    useEffect(() => {
        if (formValues?.userId) {
            setFormValues(currentFormValues => {
                const updatedFormState = {...currentFormValues};
                unset(updatedFormState, "userId");
                return updatedFormState;
            });
        }
    }, [workspaces]);

    const onFormUpdate = useCallback((_, changedField) => {
        const [key, value] = Object.entries(changedField)[0];
        setFormValues(currentFormValues => set({...currentFormValues}, key, value));
    }, []);

    return (
        <ConfigProvider direction="rtl">
            <FormModal
                content={formValues}
                className="add-new-company-domain-modal"
                confirmButtonTitle="יצירת הזמנה"
                header="יצירת הזמנה"
                visible={visible}
                onUpdate={onFormUpdate}
                onSave={onBook}
                shouldUpdateFormFromContent={true}
                fields={[
                    {
                        name: "email",
                        label: "מייל מבצע ההזמנה",
                        type: "text"
                    },
                    {
                        name: "userId",
                        label: "בחירת פרופיל",
                        type: "select",
                        disabled: isEmpty(debouncedUserEmail),
                        isLoading: isLoadingWorkspaces,
                        placeholder: "בחירת פרופיל",
                        options: workspaces?.map(({companyName, firstName, userId}) => ({
                            label: new StringBuilder(companyName, " - ").append(firstName).toString(),
                            name: userId
                        }))
                    },
                    {
                        name: "providerId",
                        label: "בחירת ספק",
                        type: "select",
                        optionFilterProp: "children",
                        showSearch: true,
                        disabled: !formValues?.userId,
                        placeholder: "בחירת ספק",
                        options: providers?.map(({businessName, providerId}) => ({
                            label: businessName,
                            name: providerId
                        }))
                    },
                    {
                        name: "productId",
                        label: "בחירת מוצר",
                        type: "select",
                        optionFilterProp: "children",
                        showSearch: true,
                        disabled: isLoadingProvider | !formValues?.providerId,
                        placeholder: "בחירת  מוצר",
                        loading: isLoadingProvider,
                        options: providerProducts?.map(({service}) => ({
                            label: service.productName,
                            name: service.productId
                        }))
                    }
                ]}
                onClose={onInnerClose}
            />
        </ConfigProvider>
    );
};
